import { useNavigate } from 'react-router-dom';
// Reuse the exact sidebar icons (same asset paths AppSidebar imports) so the
// workspace cards match the nav visually.
import inboxIcon from '/assets/new-sidebar-icons/All-Messages.svg';
import campaignsIcon from '/assets/new-sidebar-icons/Campaigns.svg';
import automationsIcon from '/assets/new-sidebar-icons/Auto-Replies-Bots.svg';
import crmIcon from '/assets/new-sidebar-icons/CRM.svg';

/**
 * "What's inside your workspace" feature grid. Each card deep-links into the
 * matching product area and uses the same icon as the sidebar, on a uniform
 * light-green tile (matching the Unified Inbox card).
 */

const ARROW = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

interface Feature {
  key: string;
  name: string;
  desc: string;
  icon: string;
  metric?: string;
  route: string;
}

const FEATURES: Feature[] = [
  {
    key: 'channels',
    name: 'Unified Inbox',
    desc: 'One place for WhatsApp, Instagram DMs and Facebook Messenger. Reply as a team, tag customers, never miss a lead.',
    icon: inboxIcon,
    metric: '3 channels available',
    route: '/home/inbox',
  },
  {
    key: 'campaigns',
    name: 'Campaigns',
    desc: 'Send WhatsApp broadcasts to your contact segments with approved templates, then track delivery and read rates.',
    icon: campaignsIcon,
    metric: 'Templates included',
    route: '/home/campaigns',
  },
  {
    key: 'automations',
    name: 'Automations',
    desc: 'Trigger-based flows: auto-reply, abandoned-cart recovery, appointment reminders — powered by Nexus AI.',
    icon: automationsIcon,
    metric: 'Visual flow builder',
    route: '/home/automation',
  },
  {
    key: 'crm',
    name: 'CRM',
    desc: 'Visual sales pipeline, contacts, notes, tickets and lead segmentation — all synced with your inbox.',
    icon: crmIcon,
    metric: 'Auto-syncs contacts',
    route: '/home/customers',
  },
];

export function WorkspaceFeatures() {
  const navigate = useNavigate();

  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-[18px] font-semibold text-[#101828]">What&apos;s inside your workspace</h3>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {FEATURES.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => navigate(f.route)}
            className="group text-left rounded-2xl bg-white border border-[#E4E7EC] p-5 hover:border-[#0EB64F]/50 hover:shadow-[0_4px_16px_rgba(16,24,40,0.06)] transition"
          >
            <div className="flex items-start gap-4">
              {/* Sidebar icon on the light-green tile (same as the Unified Inbox card) */}
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: '#0EB64F15' }}
              >
                <img src={f.icon} alt="" className="h-5 w-5 object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[15px] font-semibold text-[#101828]">{f.name}</p>
                  <span className="w-4 h-4 text-[#98A2B3] group-hover:text-[#0EB64F] group-hover:translate-x-0.5 transition">
                    {ARROW}
                  </span>
                </div>
                <p className="mt-1 text-[13px] leading-[1.55] text-[#475467]">{f.desc}</p>
                {f.metric && (
                  <span className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide rounded-full bg-[#F2F4F7] text-[#475467] px-2 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0EB64F]" />
                    {f.metric}
                  </span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
